import { forwardRef, useEffect, useRef, useState } from "react";

const DEFAULT_CLOSE_TRANSFORM = "translateY(8px) scale(0.95)";

const assignRef = (ref, value) => {
	if (!ref) return;
	if (typeof ref === "function") {
		ref(value);
		return;
	}
	ref.current = value;
};

const withTargetCloseAnimation = (WrappedComponent) => {
	const TargetCloseAnimated = forwardRef((props, forwardedRef) => {
		const {
			isAnimatingIn = false,
			animateCloseToTarget = false,
			closeAnimationTargetRef = null,
			closeAnimationFallbackTransform = DEFAULT_CLOSE_TRANSFORM,
			style,
			...rest
		} = props;
		const elementRef = useRef(null);
		const [closeTransform, setCloseTransform] = useState(
			closeAnimationFallbackTransform
		);

		useEffect(() => {
			if (isAnimatingIn) {
				setCloseTransform(closeAnimationFallbackTransform);
				return;
			}

			if (
				animateCloseToTarget &&
				closeAnimationTargetRef?.current &&
				elementRef.current
			) {
				const targetRect =
					closeAnimationTargetRef.current.getBoundingClientRect();
				const elementRect = elementRef.current.getBoundingClientRect();
				const elementCenterX = elementRect.left + elementRect.width / 2;
				const elementCenterY = elementRect.top + elementRect.height / 2;
				const targetCenterX = targetRect.left + targetRect.width / 2;
				const targetCenterY = targetRect.top + targetRect.height / 2;
				const translateX = targetCenterX - elementCenterX;
				const translateY = targetCenterY - elementCenterY;
				const scaleByWidth = targetRect.width / elementRect.width;
				const scaleByHeight = targetRect.height / elementRect.height;
				const targetScale = Math.max(
					0.08,
					Math.min(Math.min(scaleByWidth, scaleByHeight), 0.25)
				);

				setCloseTransform(
					`translate(${translateX}px, ${translateY}px) scale(${targetScale})`
				);
				return;
			}

			setCloseTransform(closeAnimationFallbackTransform);
		}, [
			animateCloseToTarget,
			closeAnimationFallbackTransform,
			closeAnimationTargetRef,
			isAnimatingIn,
		]);

		const setCombinedRef = (node) => {
			elementRef.current = node;
			assignRef(forwardedRef, node);
		};

		return (
			<WrappedComponent
				ref={setCombinedRef}
				style={{
					...style,
					transform: isAnimatingIn
						? "translate(0px, 0px) scale(1)"
						: closeTransform,
				}}
				{...rest}
			/>
		);
	});

	const componentName =
		typeof WrappedComponent === "string"
			? WrappedComponent
			: WrappedComponent.displayName ||
				WrappedComponent.name ||
				"Component";
	TargetCloseAnimated.displayName = `withTargetCloseAnimation(${componentName})`;

	return TargetCloseAnimated;
};

export default withTargetCloseAnimation;
